import { useState, useRef, useEffect } from "react";
import { Loader2, ArrowRight, CheckCircle2, Radar, AlertTriangle } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { FaceCaptureModal } from "./FaceCaptureModal";
import { useAuth } from "../context/AuthContext";

const FACE_ANGLES = [
  { key: "front", label: "Front" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

export const OnboardingModal = () => {
  const { updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [personalName, setPersonalName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [captureAngle, setCaptureAngle] = useState(null);
  const [faceAngles, setFaceAngles] = useState([]);
  const [uploadingFace, setUploadingFace] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const pollRun = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/me/sovereign-pulse/${id}`);
        setRunResult(res.data);
        if (res.data.status !== "running") clearInterval(pollRef.current);
      } catch {
        clearInterval(pollRef.current);
      }
    }, 3000);
  };

  const startStep1 = async () => {
    if (!personalName.trim() && !businessName.trim()) {
      toast.error("Enter your name or business name to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const targetType = businessName.trim() ? "brand" : "personal";
      const targetName = businessName.trim() || personalName.trim();
      const res = await axios.post(`${API}/me/sovereign-pulse`, { target_type: targetType, target_name: targetName });
      setRunResult(res.data);
      if (res.data.status === "running") pollRun(res.data.id);
      try {
        await axios.post(`${API}/me/executive-identity`, { executive_name: personalName.trim() || businessName.trim() });
      } catch (e) {
        // account identity may already exist - non-fatal
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start your first scan.");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadFace = async (blob, angle) => {
    setUploadingFace(true);
    const form = new FormData();
    form.append("file", blob, blob.name || `${angle}.jpg`);
    form.append("angle", angle);
    try {
      await axios.post(`${API}/me/executive-identity/face-sample`, form);
      setFaceAngles((prev) => [...new Set([...prev, angle])]);
      toast.success(`${angle} angle enrolled.`);
      setCaptureAngle(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process face sample.");
    } finally {
      setUploadingFace(false);
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await axios.patch(`${API}/me/onboarding-complete`);
    } catch {
      try {
        await axios.patch(`${API}/me/onboarding-complete`);
      } catch {
        // close regardless - never trap the user; it will simply re-show next login
      }
    }
    updateUser({ onboarding_completed: true });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" data-testid="onboarding-modal">
      <div className="w-full max-w-lg rounded-xl border border-warning-amber/40 bg-surface-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-heading text-lg font-bold text-white">Welcome to BrandSignal &mdash; Step {step} of 3</p>
          <div className="flex gap-1.5">
            {[1, 2, 3].map((s) => (
              <span key={s} className={`h-1.5 w-8 rounded-full ${s <= step ? "bg-warning-amber" : "bg-surface-border"}`} data-testid={`onboarding-step-dot-${s}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div data-testid="onboarding-step-1">
            <p className="mb-3 text-sm text-slate-400">Enter your name and business name to trigger your first Sovereign Pulse scan.</p>
            <Input
              placeholder="Your name"
              value={personalName}
              onChange={(e) => setPersonalName(e.target.value)}
              className="mb-2 border-surface-border bg-obsidian text-white"
              data-testid="onboarding-personal-name-input"
            />
            <Input
              placeholder="Business name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="mb-4 border-surface-border bg-obsidian text-white"
              data-testid="onboarding-business-name-input"
            />
            <Button
              onClick={startStep1}
              disabled={submitting}
              className="w-full gap-2 bg-warning-amber text-obsidian hover:bg-warning-amber/90"
              data-testid="onboarding-step1-continue-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />} Start My First Scan
            </Button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-2">
            <p className="mb-3 text-sm text-slate-400">Enroll your Face DNA (3 angles) while your first scan runs in the background.</p>
            <div className="mb-4 grid grid-cols-3 gap-2">
              {FACE_ANGLES.map(({ key, label }) => (
                <Button
                  key={key}
                  variant="outline"
                  disabled={uploadingFace}
                  onClick={() => setCaptureAngle(key)}
                  className={`gap-1 border-surface-border text-slate-200 ${faceAngles.includes(key) ? "border-truth-green text-truth-green" : ""}`}
                  data-testid={`onboarding-face-btn-${key}`}
                >
                  {faceAngles.includes(key) ? <CheckCircle2 className="h-3.5 w-3.5" /> : null} {label}
                </Button>
              ))}
            </div>
            <Button
              onClick={() => setStep(3)}
              className="w-full gap-2 bg-warning-amber text-obsidian hover:bg-warning-amber/90"
              data-testid="onboarding-step2-continue-btn"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
            <button onClick={() => setStep(3)} className="mt-2 w-full text-center text-xs text-slate-500 hover:text-white" data-testid="onboarding-skip-face-btn">
              Skip for now
            </button>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboarding-step-3">
            <p className="mb-3 text-sm text-slate-400">Your first scan result:</p>
            {!runResult || runResult.status === "running" ? (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-warning-amber/40 bg-warning-amber/5 p-4" data-testid="onboarding-scan-in-progress">
                <Loader2 className="h-4 w-4 animate-spin text-warning-amber" />
                <p className="text-sm text-warning-amber">Scan in progress - this will update automatically.</p>
              </div>
            ) : runResult.status === "completed" ? (
              <div className="mb-4 rounded-md border border-surface-border bg-obsidian p-4" data-testid="onboarding-scan-result">
                <p className="text-xs uppercase tracking-wide text-slate-500">Aggregate Sentiment</p>
                <p className="text-2xl font-bold text-white" data-testid="onboarding-scan-score">{runResult.analysis?.aggregate_sentiment_score}/100</p>
                <p className="mt-1 text-xs uppercase text-slate-400">Risk: {runResult.analysis?.aggregate_risk_level}</p>
              </div>
            ) : (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-sovereign/50 bg-sovereign/10 p-4" data-testid="onboarding-scan-failed">
                <AlertTriangle className="h-4 w-4 text-sovereign" />
                <p className="text-sm text-sovereign">Scan failed - you can rerun it anytime from your dashboard.</p>
              </div>
            )}
            <Button
              onClick={finish}
              disabled={finishing}
              className="w-full gap-2 bg-warning-amber text-obsidian hover:bg-warning-amber/90"
              data-testid="onboarding-finish-btn"
            >
              {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Done - Go to My Dashboard
            </Button>
          </div>
        )}
      </div>

      {captureAngle && (
        <FaceCaptureModal
          angle={captureAngle}
          label={FACE_ANGLES.find((a) => a.key === captureAngle)?.label || captureAngle}
          busy={uploadingFace}
          onClose={() => setCaptureAngle(null)}
          onCapture={(blob) => uploadFace(blob, captureAngle)}
        />
      )}
    </div>
  );
};
